/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { propsUtils } from '@ba-ui-toolkit/utils';
import {
	Select as CarbonSelect,
	SelectItem as CarbonSelectItem,
	SelectItemGroup as CarbonSelectItemGroup,
} from 'carbon';
import './Select.scss';
import stringResource from './nls/StringResource';

const { BaPropTypes } = propsUtils;

export default class Select extends PureComponent {
	static __Select__ = true;

	static propTypes = {
		/** Unique id for the Select component */
		id: PropTypes.string,
		/** Label displayed at the top of Select */
		label: PropTypes.string,
		/** Initial text displayed inside the Select box */
		displayLabel: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** custom width in px or % */
		customWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		/** Disables/Enables Select */
		disabled: PropTypes.bool,
		/** Passed as a prop to Select Button component */
		readOnly: PropTypes.bool,
		/** Display the entire list of items without the scrollbar */
		showAllList: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** Passed as a prop to Select Button component */
		hover: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** Passed as a prop to Select Button component */
		active: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** Sets the selected item */
		selected: PropTypes.any.isRequired,
		/** dropdown menu items. Items can be grouped using 'group' prop. Note: grouped prop should be set to true to make grouping work */
		options: PropTypes.arrayOf(
			PropTypes.shape({
				group: PropTypes.any,
				value: PropTypes.any.isRequired,
				label: PropTypes.string.isRequired,
			})
		).isRequired,
		/** If set to true, dropdown items can be grouped */
		grouped: PropTypes.bool,
		/** Icon diplayed inside the Select box */
		icon: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** Choose between two default sizes */
		size: PropTypes.oneOf(['normal', 'small']),
		/** Sets the variant for Select box (Passed as a prop to Button component) */
		variant: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** Sets the intent for Select box (Passed as a prop to Button component) */
		intent: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** Callback function on selection change */
		onChange: PropTypes.func.isRequired,
		/** Variable class names */
		variableClassName: BaPropTypes.deprecated(
			' is ignored and not applicable to carbon select'
		),
		/** If set to true, dropdown items texts will be truncated */
		truncated: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
		/** Text direction */
		textDir: BaPropTypes.deprecated(' is ignored and not applicable to carbon select'),
	};

	static defaultProps = {
		options: [],
		variant: 'frame',
		intent: 'selection',
		displayLabel: stringResource.get('selectOption'),
		size: 'normal',
		grouped: false,
		truncated: true,
		disabled: false,
		className: '',
		style: {},
	};

	_onChange = e => {
		const { onChange, options } = this.props;

		const { value } = e.target;
		const index = options.findIndex(o => String(o.value) === value);
		const option = options[index];

		onChange && onChange(option.value, option.label, index);
	};

	render() {
		// destructuring unsed props so that they can be ignored
		const {
			variant,
			intent,
			id,
			size,
			options,
			icon,
			hover,
			active,
			customWidth,
			readOnly,
			textDir,
			title,
			grouped,
			label,
			displayLabel,
			variableClassName,
			truncated,
			showAllList,
			selected,
			...others
		} = this.props;

		const optionElements = grouped
			? // attempts to mimic toolkit grouped behavior in carbon
			  options.reduce((groups, o, i) => {
					const option = (
						<CarbonSelectItem value={o.value} text={o.label} disabled={readOnly} />
					);
					// if we don't have a group we push the option directly
					if (o.group == null) {
						groups.push(option);
					} else if (i === 0 || o.group !== options[i - 1].group) {
						// if it's our first entry or we've entered a new group
						groups.push(
							o.group ? (
								<CarbonSelectItemGroup label={o.group}>
									{option}
								</CarbonSelectItemGroup>
							) : (
								option
							)
						);
					} else {
						// otherwise we're in the same group, add the option to the children of the previous group
						const element = groups[groups.length - 1];
						groups[groups.length - 1] = React.cloneElement(element, {
							children: [...React.Children.toArray(element.props.children), option],
						});
					}

					return groups;
			  }, [])
			: options.map(o => (
					<CarbonSelectItem value={o.value} text={o.label} disabled={readOnly} />
			  ));

		return (
			<CarbonSelect
				{...others}
				label={label}
				onChange={this._onChange}
				noLabel={!label}
				value={selected}
				size={size === 'small' ? 'sm' : undefined}
				className="ba-toolkit-select-compat"
				ref={el => {
					el && customWidth && el.style.setProperty('width', customWidth, 'important');
				}}
			>
				{optionElements}
			</CarbonSelect>
		);
	}
}
